/* ==========================================================================
   AUGUST ONLINE — SITE CHROME BEHAVIOUR
   Header state, announcement rotator, mobile nav, right-hand search panel,
   AJAX cart drawer.

   Runs against Shopify's real endpoints (/cart.js, /cart/add.js,
   /cart/change.js, /search/suggest.json). If `window.AUGUST_PREVIEW` is
   present the same UI is driven by that local dataset instead, so the static
   preview behaves exactly like the storefront.
   ========================================================================== */
(function () {
  'use strict';

  // Every section that needs this file loads it, so the same <script>
  // can appear a dozen times on one page. Browsers cache the download
  // but execute each tag, which would give us duplicate listeners,
  // duplicate modals and double cart adds. Run once per page.
  if (window.__augustChromeLoaded) return;
  window.__augustChromeLoaded = true;

  var root = document.documentElement;
  var preview = window.AUGUST_PREVIEW || null;
  var config = {};

  /* ---------------------------------------------------------------------
     Money
     --------------------------------------------------------------------- */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    amount = parts.join('.');
    var format = config.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  // Ask the Shopify CDN for an appropriately sized crop rather than the
  // full-resolution original that cart.js and suggest.json return.
  function sized(url, width) {
    if (!url) return '';
    var clean = url.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    if (clean.indexOf('/cdn/shop/') === -1 && clean.indexOf('cdn.shopify.com') === -1) return clean;
    return clean + (clean.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* ---------------------------------------------------------------------
     Data layer
     --------------------------------------------------------------------- */
  var api = preview
    ? preview.api
    : {
        getCart: function () {
          return fetch(window.Shopify.routes.root + 'cart.js', {
            headers: { Accept: 'application/json' }
          }).then(function (r) {
            return r.json();
          });
        },
        addItem: function (id, quantity) {
          return fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: id, quantity: quantity || 1 }] })
          }).then(function (r) {
            if (!r.ok) return r.json().then(function (e) { throw e; });
            return r.json();
          });
        },
        changeItem: function (key, quantity) {
          return fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity: quantity })
          }).then(function (r) {
            return r.json();
          });
        },
        search: function (query) {
          var url =
            window.Shopify.routes.root +
            'search/suggest.json?q=' +
            encodeURIComponent(query) +
            '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';
          return fetch(url, { headers: { Accept: 'application/json' } })
            .then(function (r) {
              return r.json();
            })
            .then(function (data) {
              var items = (data.resources && data.resources.results.products) || [];
              return items.map(function (p) {
                return {
                  title: p.title,
                  url: p.url,
                  vendor: p.vendor,
                  image: p.featured_image ? p.featured_image.url : p.image,
                  price: p.price,
                  compare_at_price: p.compare_at_price_max || null,
                  available: p.available
                };
              });
            });
        }
      };

  /* ---------------------------------------------------------------------
     Drawers
     --------------------------------------------------------------------- */
  var scrim = null;
  var openDrawer = null;
  var lastFocus = null;

  function ensureScrim() {
    scrim = document.querySelector('[data-au-scrim]');
    if (scrim) return scrim;
    scrim = document.createElement('div');
    scrim.className = 'au-scrim';
    scrim.setAttribute('data-au-scrim', '');
    document.body.appendChild(scrim);
    scrim.addEventListener('click', closeDrawer);
    return scrim;
  }

  function trapFocus(event) {
    if (!openDrawer || event.key !== 'Tab') return;
    var focusables = openDrawer.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') closeDrawer();
    trapFocus(event);
  }

  function showDrawer(id) {
    var drawer = document.querySelector('[data-au-drawer="' + id + '"]');
    if (!drawer) return;
    if (openDrawer && openDrawer !== drawer) closeDrawer(true);

    lastFocus = document.activeElement;
    ensureScrim().classList.add('is-open');
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('au-lock');
    openDrawer = drawer;
    document.addEventListener('keydown', onKeydown);

    var focusTarget = drawer.querySelector('[data-au-autofocus]') || drawer.querySelector('button, a, input');
    if (focusTarget) {
      window.setTimeout(function () {
        focusTarget.focus();
      }, 260);
    }

    drawer.dispatchEvent(new CustomEvent('au:drawer:open'));
  }

  function closeDrawer(silent) {
    if (!openDrawer) return;
    openDrawer.classList.remove('is-open');
    openDrawer.setAttribute('aria-hidden', 'true');
    openDrawer = null;
    if (!silent) {
      if (scrim) scrim.classList.remove('is-open');
      document.body.classList.remove('au-lock');
      document.removeEventListener('keydown', onKeydown);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
  }

  /* ---------------------------------------------------------------------
     Cart drawer
     --------------------------------------------------------------------- */
  var cartState = null;

  function cartNodes() {
    return {
      drawer: document.querySelector('[data-au-drawer="cart"]'),
      body: document.querySelector('[data-au-cart-body]'),
      foot: document.querySelector('[data-au-cart-foot]'),
      counts: document.querySelectorAll('[data-au-cart-count]')
    };
  }

  function renderCart(cart) {
    cartState = cart;
    var n = cartNodes();
    if (!n.body) return;

    n.counts.forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });

    if (!cart.items || cart.items.length === 0) {
      n.body.innerHTML =
        '<div class="au-cart__empty"><p>Your bag is empty</p>' +
        '<a class="au-cart__checkout" href="' +
        (config.shopAllUrl || '/collections/all') +
        '">Browse the archive</a></div>';
      if (n.foot) n.foot.hidden = true;
      return;
    }

    var threshold = config.freeShippingThreshold || 0;
    var ship = '';
    if (threshold > 0) {
      var remaining = threshold - cart.total_price;
      var pct = Math.min(100, (cart.total_price / threshold) * 100);
      ship =
        '<div class="au-cart__ship"><p class="au-cart__ship-text">' +
        (remaining > 0
          ? formatMoney(remaining) + ' away from free shipping'
          : 'Free shipping unlocked') +
        '</p><div class="au-cart__ship-track"><div class="au-cart__ship-fill" style="width:' +
        pct +
        '%"></div></div></div>';
    }

    var rows = cart.items
      .map(function (item) {
        var img = item.image
          ? '<img src="' + sized(item.image, 240) + '" alt="" loading="lazy">'
          : '';
        var variant =
          item.variant_title && item.variant_title !== 'Default Title'
            ? '<span class="au-cart__variant">' + item.variant_title + '</span>'
            : '';
        return (
          '<li class="au-cart__item" data-au-line="' + item.key + '">' +
          '<div class="au-cart__thumb">' + img + '</div>' +
          '<div>' +
          (item.vendor ? '<span class="au-cart__vendor">' + item.vendor + '</span>' : '') +
          '<a class="au-cart__title" href="' + item.url + '">' + item.product_title + '</a>' +
          variant +
          '<div class="au-cart__row">' +
          '<span class="au-qty">' +
          '<button type="button" data-au-qty="-1" aria-label="Reduce quantity">&minus;</button>' +
          '<span>' + item.quantity + '</span>' +
          '<button type="button" data-au-qty="1" aria-label="Increase quantity">+</button>' +
          '</span>' +
          '<span class="au-cart__price">' + formatMoney(item.line_price) + '</span>' +
          '</div>' +
          '<button type="button" class="au-cart__remove" data-au-remove>Remove</button>' +
          '</div></li>'
        );
      })
      .join('');

    n.body.innerHTML = ship + '<ul class="au-cart__list">' + rows + '</ul>';

    if (n.foot) {
      n.foot.hidden = false;
      var total = n.foot.querySelector('[data-au-cart-total]');
      if (total) total.textContent = formatMoney(cart.total_price);
    }
  }

  function refreshCart() {
    return api
      .getCart()
      .then(renderCart)
      .catch(function () {
        /* leave the last known state on screen */
      });
  }

  function busy(state) {
    var drawer = document.querySelector('[data-au-drawer="cart"]');
    if (drawer) drawer.classList.toggle('is-busy', !!state);
  }

  function bindCart() {
    var body = document.querySelector('[data-au-cart-body]');
    if (!body || body.dataset.auBound) return;
    body.dataset.auBound = 'true';

    body.addEventListener('click', function (event) {
      var line = event.target.closest('[data-au-line]');
      if (!line) return;
      var key = line.getAttribute('data-au-line');
      var item = cartState.items.filter(function (i) {
        return i.key === key;
      })[0];
      if (!item) return;

      var step = event.target.closest('[data-au-qty]');
      var remove = event.target.closest('[data-au-remove]');
      if (!step && !remove) return;

      var quantity = remove ? 0 : item.quantity + parseInt(step.getAttribute('data-au-qty'), 10);
      if (quantity < 0) quantity = 0;

      busy(true);
      api
        .changeItem(key, quantity)
        .then(renderCart)
        .catch(refreshCart)
        .then(function () {
          busy(false);
        });
    });
  }

  // Quick-shop forms anywhere on the page post into the drawer instead of
  // reloading the page. Without JS they still submit normally.
  function bindAddForms() {
    document.addEventListener('submit', function (event) {
      var form = event.target.closest('form[data-au-add]');
      if (!form) return;
      var idField = form.querySelector('[name="id"]');
      if (!idField) return;

      event.preventDefault();
      var button = form.querySelector('button[type="submit"]');
      if (button) button.disabled = true;

      api
        .addItem(idField.value, 1)
        .then(function () {
          return api.getCart();
        })
        .then(function (cart) {
          renderCart(cart);
          showDrawer('cart');
        })
        .catch(function () {
          form.submit();
        })
        .then(function () {
          if (button) button.disabled = false;
        });
    });
  }

  /* ---------------------------------------------------------------------
     Search panel
     --------------------------------------------------------------------- */
  function bindSearch() {
    var drawer = document.querySelector('[data-au-drawer="search"]');
    if (!drawer || drawer.dataset.auBound) return;
    drawer.dataset.auBound = 'true';

    var input = drawer.querySelector('[data-au-search-input]');
    var results = drawer.querySelector('[data-au-search-results]');
    var intro = drawer.querySelector('[data-au-search-intro]');
    var clear = drawer.querySelector('[data-au-search-clear]');
    var timer = null;

    function setState(message) {
      results.innerHTML = '<p class="au-srch__state">' + message + '</p>';
    }

    function render(items, query) {
      if (!items.length) {
        setState('Nothing found for “' + query + '”');
        return;
      }
      var rows = items
        .map(function (p) {
          var price =
            typeof p.price === 'number'
              ? formatMoney(p.price) +
                (p.compare_at_price && p.compare_at_price > p.price
                  ? '<s>' + formatMoney(p.compare_at_price) + '</s>'
                  : '')
              : p.price || '';
          return (
            '<li><a class="au-srch__row" href="' + p.url + '">' +
            '<span class="au-srch__thumb">' +
            (p.image ? '<img src="' + sized(p.image, 200) + '" alt="" loading="lazy">' : '') +
            '</span><span>' +
            (p.vendor ? '<span class="au-srch__row-vendor">' + p.vendor + '</span>' : '') +
            '<span class="au-srch__row-title">' + p.title + '</span>' +
            '<span class="au-srch__row-price">' + price + '</span>' +
            '</span></a></li>'
          );
        })
        .join('');

      results.innerHTML =
        '<p class="au-srch__label">Pieces</p><ul class="au-srch__results">' +
        rows +
        '</ul><p style="margin-top:1.5rem"><a class="au-srch__chip" href="' +
        (config.searchUrl || '/search') +
        '?q=' +
        encodeURIComponent(query) +
        '">See all results</a></p>';
    }

    function run(query) {
      if (query.length < 2) {
        results.innerHTML = '';
        if (intro) intro.hidden = false;
        return;
      }
      if (intro) intro.hidden = true;
      setState('Searching…');
      api
        .search(query)
        .then(function (items) {
          if (input.value.trim() !== query) return;
          render(items, query);
        })
        .catch(function () {
          setState('Search is unavailable right now');
        });
    }

    input.addEventListener('input', function () {
      window.clearTimeout(timer);
      var query = input.value.trim();
      timer = window.setTimeout(function () {
        run(query);
      }, 240);
    });

    if (clear) {
      clear.addEventListener('click', function () {
        input.value = '';
        input.focus();
        run('');
      });
    }

    drawer.querySelectorAll('[data-au-search-term]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        input.value = chip.getAttribute('data-au-search-term');
        input.focus();
        run(input.value);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Announcement rotator
     --------------------------------------------------------------------- */
  function bindAnnouncement() {
    var bar = document.querySelector('[data-au-announce]');
    if (!bar || bar.dataset.auBound) return;
    bar.dataset.auBound = 'true';

    var items = bar.querySelectorAll('.au-ann__item');
    if (items.length < 2) return;
    var index = 0;
    var delay = parseInt(bar.getAttribute('data-au-announce'), 10) || 5;

    window.setInterval(function () {
      items[index].classList.remove('is-active');
      index = (index + 1) % items.length;
      items[index].classList.add('is-active');
    }, delay * 1000);
  }

  /* ---------------------------------------------------------------------
     Mobile nav
     --------------------------------------------------------------------- */
  function bindMobileNav() {
    document.querySelectorAll('[data-au-mnav-toggle]').forEach(function (button) {
      if (button.dataset.auBound) return;
      button.dataset.auBound = 'true';
      button.addEventListener('click', function () {
        var expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!expanded));
        var panel = document.getElementById(button.getAttribute('aria-controls'));
        if (panel) panel.classList.toggle('is-open', !expanded);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Header scroll state
     --------------------------------------------------------------------- */
  var ticking = false;

  function onFrame() {
    ticking = false;
    var header = document.querySelector('[data-au-header]');
    if (!header) return;
    var y = window.pageYOffset || document.documentElement.scrollTop;
    header.classList.toggle('au-hdr-root--stuck', y > 24);
  }

  function requestFrame() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(onFrame);
  }

  function measureHeader() {
    var header = document.querySelector('[data-au-header]');
    if (!header) return;
    root.style.setProperty('--au-chrome-h', Math.round(header.offsetHeight) + 'px');
  }

  /* ---------------------------------------------------------------------
     Localisation selects submit their own form
     --------------------------------------------------------------------- */
  function bindLocalization() {
    document.querySelectorAll('[data-au-loc-select]').forEach(function (select) {
      if (select.dataset.auBound) return;
      select.dataset.auBound = 'true';
      select.addEventListener('change', function () {
        if (select.form) select.form.submit();
      });
    });
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function readConfig() {
    var node = document.querySelector('[data-au-chrome-config]');
    if (!node) return;
    try {
      config = JSON.parse(node.textContent);
    } catch (e) {
      config = {};
    }
  }

  function boot() {
    readConfig();
    if (preview && preview.config) {
      Object.keys(preview.config).forEach(function (key) {
        config[key] = preview.config[key];
      });
    }

    ensureScrim();
    bindAnnouncement();
    bindMobileNav();
    bindSearch();
    bindCart();
    bindAddForms();
    bindLocalization();
    measureHeader();
    requestFrame();
    refreshCart();

    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-au-open]');
      if (opener) {
        event.preventDefault();
        showDrawer(opener.getAttribute('data-au-open'));
        return;
      }
      if (event.target.closest('[data-au-close]')) {
        event.preventDefault();
        closeDrawer();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.addEventListener('scroll', requestFrame, { passive: true });
  window.addEventListener('resize', measureHeader);

  document.addEventListener('shopify:section:load', function () {
    bindAnnouncement();
    bindMobileNav();
    bindSearch();
    bindCart();
    bindLocalization();
    measureHeader();
    refreshCart();
  });

  // Expose a tiny surface so other sections can drive the drawers.
  window.August = {
    openDrawer: showDrawer,
    closeDrawer: closeDrawer,
    refreshCart: refreshCart,
    formatMoney: formatMoney,
    addToCart: function (id, quantity) {
      return api.addItem(id, quantity || 1);
    }
  };
})();
